import { Alert, Button, Card, Form, Input, Radio, Typography } from "antd";
import { useEffect, useState, type ComponentProps } from "react";
import { useParams } from "react-router";
import { DatePicker as ZhDatePicker, InputNumber as ZhInputNumber } from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT } from "@/config";
import { PURCHASE_TYPE } from "@/meta/purchase/intend";

// beta.13 的运行时兼容旧 Vue MoneyInput，但类型声明未暴露 controls 字段。
type DemandInputNumberProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const integerInputProps: DemandInputNumberProps = { controls: false, min: 0, precision: 0 };
const quantityInputProps: DemandInputNumberProps = { controls: false, min: 1, precision: 0 };
const ratioInputProps: DemandInputNumberProps = { controls: false, max: 99.99, min: 0.01, precision: 2 };

export type IntendedDemandFormData = Record<string, any>;

/** 复用 Vue 采购意向详情接口，供编辑表单回显。 */
export async function loadIntendedDemandForm(id: number | string): Promise<IntendedDemandFormData> {
    const { data } = await request.service.get<IntendedDemandFormData>(`/admin/purchase-demand/vo/${id}`);
    return data;
}

/** 把日期控件的日期值转换为 Vue 接口要求的完整日期时间。 */
function toDateTime(value?: string) {
    if (!value) return value;
    return value.length === 10 ? `${value} 00:00:00` : value;
}

/** 采购意向新增/编辑最小闭环，保留 Vue 字段、校验和保存 payload。 */
export function IntendedDemandFormPage({ intendId: intendIdProp }: { intendId?: number | string }) {
    const { intendId: routeIntendId } = useParams<{ intendId?: string }>();
    const intendId = intendIdProp ?? routeIntendId;
    const [form] = Form.useForm();
    // 监听采购类型，保持 Vue 表单中联合采购专属字段的显隐状态。
    const [purType, setPurType] = useState(PURCHASE_TYPE.INDIRECT.code);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (!intendId) return;
        void loadIntendedDemandForm(intendId)
            .then(data => {
                const formData = data.purchaseDemand ?? data;
                form.setFieldsValue(formData);
                setPurType(Number(formData.purType));
            })
            .catch(() => setError("采购意向详情加载失败"));
    }, [form, intendId]);

    /** 按 Vue 组合后的表单结构提交，移除前端仅用于展示的 receiveArea。 */
    const submit = async (values: IntendedDemandFormData) => {
        setError("");
        const payload = {
            ...values,
            purType: Number(values.purType),
            productId: Number(values.productId),
            buyerId: Number(values.buyerId),
            purQuantity: Number(values.purQuantity),
            purRatio: values.purRatio === undefined ? undefined : Number(values.purRatio),
            provinceCode: String(values.provinceCode),
            cityCode: String(values.cityCode),
            regionCode: String(values.regionCode),
            deliveryTime: toDateTime(values.deliveryTime),
            serviceCosts: []
        };
        try {
            if (intendId) await request.service.put(`/admin/purchase-demand/${intendId}`, payload);
            else await request.service.post("/admin/purchase-demand", payload);
            setMessage(intendId ? "采购意向编辑成功" : "采购意向新增成功");
        } catch {
            setError(intendId ? "采购意向编辑失败" : "采购意向新增失败");
        }
    };

    return (
        <section aria-label="intended-demand-form-page">
            <Card>
                <Typography.Title level={4}>{intendId ? "编辑采购意向" : "新增采购意向"}</Typography.Title>
                {error ? <Alert role="alert" title={error} type="error" /> : null}
                {message ? <span role="status">{message}</span> : null}
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ purType: PURCHASE_TYPE.INDIRECT.code }}
                    onValuesChange={changedValues => {
                        if (changedValues.purType !== undefined) setPurType(Number(changedValues.purType));
                        // Vue 切换到直采时会清空联合采购专属的预付款比例。
                        if (
                            changedValues.purType !== undefined &&
                            PURCHASE_TYPE.DIRECT.match(Number(changedValues.purType))
                        ) {
                            form.setFieldValue("purRatio", undefined);
                        }
                    }}
                    onFinish={submit}
                >
                    <Form.Item label="类型" name="purType" rules={[{ required: true, message: "请选择采购类型" }]}>
                        <Radio.Group>
                            <Radio value={1}>联合采购</Radio>
                            <Radio value={2}>直采</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item label="商品ID" name="productId" rules={[{ required: true, message: "请选择商品" }]}>
                        <ZhInputNumber {...integerInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="采购商账号ID"
                        name="buyerId"
                        rules={[{ required: true, message: "请选择客户账号信息" }]}
                    >
                        <ZhInputNumber {...integerInputProps} />
                    </Form.Item>
                    <Form.Item label="联系人" name="contact" rules={[{ required: true, message: "请输入联系人" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="联系方式" name="mobile" rules={[{ required: true, message: "请输入联系方式" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="省级区域编码"
                        name="provinceCode"
                        rules={[{ required: true, message: "请选择收货区域" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="城市区域编码"
                        name="cityCode"
                        rules={[{ required: true, message: "请选择收货区域" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="区县区域编码"
                        name="regionCode"
                        rules={[{ required: true, message: "请选择收货区域" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="提货日期"
                        name="deliveryTime"
                        rules={[{ required: true, message: "请选择提货日期" }]}
                    >
                        <ZhDatePicker
                            aria-label="提货日期"
                            format={DATE_PICKER_FORMAT}
                            placeholder="选择日期"
                            type="date"
                            valueFormat={DATE_PICKER_FORMAT}
                        />
                    </Form.Item>
                    <Form.Item
                        label="采购数量"
                        name="purQuantity"
                        rules={[{ required: true, message: "请输入采购数量" }]}
                    >
                        <ZhInputNumber {...quantityInputProps} addonAfter="吨" />
                    </Form.Item>
                    {PURCHASE_TYPE.INDIRECT.match(Number(purType)) ? (
                        <Form.Item
                            label="采购商预付款比例"
                            name="purRatio"
                            rules={[{ required: true, message: "请输入可出资比例" }]}
                        >
                            <ZhInputNumber {...ratioInputProps} addonAfter="%" />
                        </Form.Item>
                    ) : null}
                    <Form.Item label="需求描述" name="remark">
                        <Input.TextArea />
                    </Form.Item>
                    <Button autoInsertSpace={false} htmlType="submit" type="primary">
                        提交
                    </Button>
                </Form>
            </Card>
        </section>
    );
}

export default IntendedDemandFormPage;
